'use client';

import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { GatewayTopbar } from '../../../components/common/top-bar/GatewayTopbar';
import GatewayHeader from '../../../components/common/header/GatewayHeader';
import GatewayCard, {
	GatewayCardType,
	GATEWAY_CARD_BORDER_RADIUS,
	MOBILE_GATEWAY_CARD_BORDER_RADIUS,
	PRIMARY_CARD_HEIGHT_DESKTOP_XL,
	PRIMARY_CARD_SUBTRACTION_HEIGHT_DESKTOP,
	PRIMARY_CARD_HEIGHT_TABLET_XL,
} from '../../../components/common/cards/Gateway/GatewayCard';
import { AirportCodes } from '../../../types/airports';
import {
	ContentWrap,
	Css,
	IUrlLinkItem,
	Responsive,
	ResponsivePicture,
} from '../../../ui';
import defaultHeaderImage from '../../../assets/default-landing-header-image.webp';
import { sectionLinks } from '../domain/sectionLinks';
import { useCommonData } from '../../../common-data/useCommonData';
import { getFooterLinksFromEntry } from '../../../utils/getFooterLinksFromEntry';
import { SilentErrorBoundary } from '../../shared/components/error/SilentErrorBoundary';
import { Footer } from '../../../components/common/footer/Footer';
import { ISocialLinksProps } from '../../../components/common/social-links/SocialLinks';
import { IFooterLinkSections } from '../../../components/common/footer/types';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { ISiteSectionEntry } from '../../../contentful/types/ISiteSection';
import { IAsset } from '../../../contentful';

interface IGatewayPageProps {
	gatewaySiteSection?: ISiteSectionEntry;
	gatewayImage?: IAsset;
	gatewayVideoDesktop?: IAsset;
	gatewayVideoMobile?: IAsset;
}

export default function GatewayPage({
	gatewaySiteSection,
	gatewayImage,
	gatewayVideoDesktop,
	gatewayVideoMobile,
}: IGatewayPageProps) {
	const { pathToPageIdMap } = useCommonData();

	const isTablet = !!Responsive.useMatchesBreakpoint('tablet');
	const { windowHeight } = useWindowSize();

	const videoDesktopUrl = gatewayVideoDesktop?.fields.file.url;
	const videoDesktopFormat = gatewayVideoDesktop?.fields.file.contentType;
	const videoMobileUrl = gatewayVideoMobile?.fields.file.url;
	const videoMobileFormat = gatewayVideoMobile?.fields.file.contentType;

	const shouldRenderFallbackImage =
		(!gatewayVideoDesktop && !gatewayVideoMobile) ||
		(!isTablet && !gatewayVideoDesktop) ||
		(isTablet && !gatewayVideoMobile);

	const footer = gatewaySiteSection?.fields?.footer;
	let linkSections;

	if (footer) {
		linkSections = getFooterLinksFromEntry(footer, pathToPageIdMap);
	}

	return (
		<div>
			<title>Home | Houston Airport System</title>

			<GatewayTopbar />

			<GatewayHeader />

			<GatewayCardsContainer pageHasFooter={!!footer}>
				{sectionLinks.map((link) => (
					<GatewayCard
						key={link.id}
						link={link}
						type={
							link.id === AirportCodes.IAH || link.id === AirportCodes.HOU
								? GatewayCardType.Primary
								: GatewayCardType.Secondary
						}
					/>
				))}

				{/* Desktop video */}
				{!isTablet && gatewayVideoDesktop && (
					<StyledGatewayVideo
						loop
						controls={false}
						muted
						autoPlay
						playsInline
						windowHeight={windowHeight}
					>
						<source src={videoDesktopUrl} type={videoDesktopFormat} />
					</StyledGatewayVideo>
				)}

				{/* Mobile video */}
				{isTablet && gatewayVideoMobile && (
					<StyledGatewayVideo
						loop
						controls={false}
						muted
						autoPlay
						playsInline
						windowHeight={windowHeight}
					>
						<source src={videoMobileUrl} type={videoMobileFormat} />
					</StyledGatewayVideo>
				)}

				{/* Fallback image */}
				{shouldRenderFallbackImage && (
					<StyledResponsivePicture
						source={gatewayImage?.fields.file.url || defaultHeaderImage}
						alt="Airport Image"
						rounded
					/>
				)}
			</GatewayCardsContainer>

			{footer && (
				<SilentErrorBoundary>
					<Footer
						appStoreLinks={linkSections?.appStoreLinks as ISocialLinksProps}
						linkSections={linkSections?.linkSections as IFooterLinkSections}
						socialLinks={linkSections?.socialLinks as ISocialLinksProps}
						finePrint={linkSections?.finePrintLinks.links as IUrlLinkItem[]}
						featuredLink={linkSections?.featuredLink}
					/>
				</SilentErrorBoundary>
			)}
		</div>
	);
}

const GatewayCardsContainer = styled(ContentWrap)<{ pageHasFooter: boolean }>`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	grid-template-rows: auto;
	column-gap: ${Css.pxToRem(18)};
	row-gap: ${Css.pxToRem(18)};
	grid-template-areas:
		'iah hou fly image'
		'iah hou efd image'
		'iah hou spaceport image';
	margin-bottom: ${({ pageHasFooter }) =>
		pageHasFooter ? Css.pxToRem(90) : Css.pxToRem(10)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-bottom: ${({ pageHasFooter }) =>
			pageHasFooter ? Css.pxToRem(90) : Css.pxToRem(10)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		grid-template-columns: repeat(3, 1fr);
		grid-template-areas:
			'iah hou fly'
			'iah hou efd'
			'iah hou spaceport'
			'image image image';
		margin-bottom: ${Css.pxToRem(40)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
		grid-template-areas:
			'iah iah iah'
			'hou hou hou'
			'fly efd spaceport'
			'image image image';
		column-gap: ${Css.pxToRem(8)};
		row-gap: ${Css.pxToRem(8)};
	}
`;

const StyledResponsivePicture = styled(ResponsivePicture)`
	grid-area: image;

	img {
		height: 100%;
		object-fit: cover;
		border-radius: ${GATEWAY_CARD_BORDER_RADIUS};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: 132px;
			border-radius: ${MOBILE_GATEWAY_CARD_BORDER_RADIUS};
		}
	}
`;

const StyledGatewayVideo = styled('video', {
	shouldForwardProp: isPropValid,
})<{ windowHeight?: number }>`
	grid-area: image;
	max-height: ${PRIMARY_CARD_HEIGHT_DESKTOP_XL};
	border-radius: ${GATEWAY_CARD_BORDER_RADIUS};
	width: 100%;
	object-fit: cover;
	object-position: center;

	@media (${Responsive.getMediaQueryForBreakpoint('desktop')}) {
		height: ${({ windowHeight }) =>
			windowHeight
				? `calc(${Css.pxToRem(windowHeight)} - ${Css.pxToRem(
						PRIMARY_CARD_SUBTRACTION_HEIGHT_DESKTOP,
				  )})`
				: 'auto'};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		height: ${PRIMARY_CARD_HEIGHT_TABLET_XL};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		height: auto;
	}
`;
